import type { Theme } from '@material-ui/core';
import {
  createStyles,
  DialogActions,
  DialogContent,
  Grid,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CustomPalette, Icons, SidePanel, TextField } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import type { HeirachyResult } from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import { ComponentAreaInfo } from '../../rxdb/deficiencyList/queryBuilder';
import CustomButton from '../CustomButton/CustomButton';
import GenericSidePanelHeader from '../GenericSidePanelHeader';
import RenderTreeView from './TreeView';

export interface DeleteModalConfig {
  title: string;
  bodyText: string;
  cancelButtonText: string;
  deleteButtonText: string;
  maxHeight?: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      padding: '16px',
      width: '400px',
      backgroundColor: CustomPalette.elevation.dp4Solid,
      overflow: 'hidden',
    },
    dialogActions: {
      borderTop: `1px solid ${theme.palette.background.default}`,
      backgroundColor: CustomPalette.elevation.dp4Solid,
    },
    searchGrid: {
      padding: '16px',
      borderRadius: '8px 8px 0px 0px',
      backgroundColor: 'rgb(54, 54, 54)',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      '& .innerSearchGrid': {
        alignSelf: 'center',
      },
    },
    treeGrid: {
      padding: '16px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      overflow: 'auto',
      height: '90%',
    },
  }),
);

interface Props extends React.ComponentPropsWithoutRef<'div'> {
  open: boolean;
  treeData: HeirachyResult[];
  onClose: () => void;
  onSave: (value: [ComponentAreaInfo]) => void;
  title: string;
  defaultSelected: [ComponentAreaInfo];
  searchText: string;
  handleTreeDataSearch: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  componentVersionId?: number | null;
}

const GenericSidePanel: React.FC<Props> = ({
  open,
  treeData,
  onClose,
  onSave,
  title,
  defaultSelected,
  searchText,
  handleTreeDataSearch,
  componentVersionId,
}: Props) => {
  const classes = useStyles();
  const [tempComponentLocation, setTempComponentLocation] = useState<ComponentAreaInfo[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);

  useEffect(() => {
    setTempComponentLocation(defaultSelected || []);
  }, [defaultSelected]);

  const findPropertyHelper = (
    obj: HeirachyResult | [HeirachyResult] | HeirachyResult[] | string | number | null | boolean,
    key: string | number,
    list: number[],
  ): number[] => {
    if (!obj) {
      return list;
    }
    if (obj instanceof Array) {
      let listData: number[] = [];
      for (const i in obj) {
        listData = list.concat(findPropertyHelper(obj[i], key, []));
      }
      return listData;
    }
    if ((obj as HeirachyResult)[key as keyof HeirachyResult])
      list.push((obj as HeirachyResult)[key as keyof HeirachyResult] as number);

    if (typeof obj == 'object' && obj !== null) {
      const children = Object.keys(obj);
      if (children.length > 0) {
        let listData: number[] = [];
        for (let i = 0; i < children.length; i++) {
          listData = list.concat(
            findPropertyHelper(
              (obj as HeirachyResult)[children[i] as keyof HeirachyResult],
              key,
              [],
            ),
          );
        }
        return listData;
      }
    }
    return list;
  };

  const findNodes = (array: HeirachyResult[], key: string) => {
    return findPropertyHelper(array, key, []);
  };

  useEffect(() => {
    let nodes =
      searchText.length > 3
        ? findNodes(treeData, 'id').map(String)
        : treeData &&
          treeData.filter((y: HeirachyResult) => y.id).map((x: HeirachyResult) => x.id.toString());
    nodes = nodes && nodes.filter((y: string) => y).map((x: string) => x.toString());
    setExpandedNodes(nodes);
  }, [searchText, treeData]);

  const openDiscardEditsModel = () => {
    setTempComponentLocation(defaultSelected || []);
    onClose();
  };

  const toggleNode = (nodeId: string[]) => {
    setExpandedNodes(nodeId);
  };

  return (
    <SidePanel
      data-testid="cl-generic-side-panel"
      open={open}
      onClose={openDiscardEditsModel}
      transitionDuration={200}
      disableBackdropClick
    >
      <GenericSidePanelHeader title={title} onClose={openDiscardEditsModel} />

      <DialogContent className={classes.dialogContent}>
        <Grid item container xs={12} className={classes.searchGrid}>
          <Grid item xs={12} className="innerSearchGrid">
            <TextField
              inputProps={{
                'data-testid': 'search-location',
              }}
              noMinHeight
              variant="outlined"
              startAdornment={
                <InputAdornment position="start">
                  <Icons.SearchFeather color="rgba(255, 255, 255, 0.6)" />
                </InputAdornment>
              }
              placeholder="Search component location"
              onChange={handleTreeDataSearch}
              size="small"
            />
          </Grid>
        </Grid>
        {treeData?.length > 0 ? (
          <Grid item container xs={12} className={classes.treeGrid}>
            <Grid item xs={12}>
              {treeData && treeData.length > 0 && (
                <RenderTreeView
                  treeViewData={treeData}
                  tempComponentLocation={tempComponentLocation}
                  onChange={setTempComponentLocation}
                  expanded={expandedNodes}
                  onNodeToggle={toggleNode}
                  componentVersionId={componentVersionId}
                />
              )}
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={openDiscardEditsModel}
              data-cy="cancelDeficiency"
              noMinHeight
            >
              Cancel
            </Button>
          </Grid>
          <CustomButton
            onClose={() => onSave(tempComponentLocation as [ComponentAreaInfo])}
            disabled={!tempComponentLocation?.length}
            buttonText="Add"
          />
        </Grid>
      </DialogActions>
    </SidePanel>
  );
};

export default GenericSidePanel;
